/**
 * The NexERP app ecosystem: every screen of the ERP, grouped into apps.
 *
 * This is the one map of where things live. The launcher draws the apps from
 * it, the contextual navigation draws the current app's pages, the command
 * center searches it and the shell reads it to know which app a URL belongs
 * to. It replaces the single long sidebar; the destinations, their
 * permissions and the optional-module rule are carried over unchanged.
 *
 * RULES CARRIED OVER FROM THE SIDEBAR
 *
 *  - `permissions` lists the codes that make an item visible (any one is
 *    enough). An item with none is visible to every member. A UI courtesy,
 *    not the enforcement: every route and API call is checked server-side.
 *  - `module` on an app names the OPTIONAL module it belongs to: the app is
 *    shown only while that module is switched on for the organization
 *    (Settings > Modules, where anyone can request one). Core apps carry no
 *    key. Until the module list has answered nothing is hidden for being off.
 *
 * FIELDS
 *
 *  app.home      where opening the app lands, when the caller may see it;
 *                otherwise the app opens on its first visible page.
 *  item.end      matches the path exactly, not as a prefix.
 *  item.match    extra path prefixes that belong to the item (detail pages
 *                reached from it, sibling routes).
 *  item.shared   a page that belongs to another app and is listed here as a
 *                convenience. It keeps you in THIS app when you arrive from
 *                it, but never claims the URL on its own.
 *  item.badge    'approvals' shows the pending purchase approvals count.
 *  item.children sub-pages shown beneath the item.
 *  item.keywords extra words the command center matches on.
 */

export const APPS = [
  {
    key: 'procurement',
    name: 'Procurement',
    description: 'Suppliers, requests, RFQs, orders and vendor bills',
    tagline: 'Buy from your suppliers',
    icon: 'app-procurement',
    home: '/procurement',
    sections: [
      {
        items: [
          { to: '/procurement', label: 'Overview', icon: 'overview', permissions: ['purchase.read'], end: true },
        ],
      },
      {
        label: 'Buying',
        items: [
          {
            to: '/purchase-requests',
            label: 'Purchase requests',
            icon: 'purchase-request',
            permissions: ['purchase.read'],
            badge: 'approvals',
            keywords: 'pr requisition indent',
          },
          { to: '/rfqs', label: 'RFQs', icon: 'rfq', permissions: ['rfq.read'], keywords: 'request for quotation tender bids' },
          {
            to: '/rfqs/intake/submissions',
            label: 'External enquiries',
            icon: 'enquiry',
            permissions: ['rfq.intake.read'],
            keywords: 'intake submissions website form',
          },
          { to: '/purchase-orders', label: 'Purchase orders', icon: 'purchase-order', permissions: ['purchase.read'], keywords: 'po' },
          { to: '/goods-receipts', label: 'Goods receipts', icon: 'goods-receipt', permissions: ['purchase.read'], keywords: 'grn receiving' },
        ],
      },
      {
        label: 'Payables',
        items: [
          {
            to: '/purchase-bills',
            label: 'Vendor bills',
            icon: 'bill',
            permissions: ['purchase.bill.read'],
            end: true,
            // Bills themselves live under /purchase-bills/:id; the exceptions
            // list is its own item and wins by being the longer match.
            match: ['/purchase-bills/'],
            keywords: 'invoice supplier bill three-way match',
          },
          {
            to: '/purchase-bills/exceptions',
            label: 'Match exceptions',
            icon: 'exception',
            permissions: ['purchase.bill.read'],
          },
        ],
      },
      {
        label: 'Supply base',
        items: [
          { to: '/suppliers', label: 'Suppliers', icon: 'supplier', permissions: ['supplier.read'], keywords: 'vendors' },
          { to: '/analytics/aging-spend?tab=spend', label: 'Spend Analysis & PPV', icon: 'analytics', permissions: ['purchase.read'], keywords: 'spend pareto ppv procurement variance price' },
          { to: '/analytics/aging-spend?tab=spend&subtab=scorecard', label: 'Vendor Performance Scorecard', icon: 'report', permissions: ['purchase.read'], keywords: 'vendor otd performance scorecard supplier quality' },
        ],
      },
      {
        label: 'Setup',
        items: [
          {
            to: '/settings/purchasing',
            label: 'Purchasing settings',
            icon: 'settings-sliders',
            permissions: ['purchase.settings.manage', 'purchase.read'],
            keywords: 'tolerances receiving matching',
          },
          { to: '/settings/rfq', label: 'RFQ settings', icon: 'settings', permissions: ['rfq.config.read'], keywords: 'rfq types workflow approvals fields' },
          {
            to: '/rfqs/intake/forms',
            label: 'External RFQ forms',
            icon: 'form-builder',
            permissions: ['rfq.intake.read'],
            keywords: 'intake form builder website',
          },
        ],
      },
    ],
  },

  {
    // Everything is readable with inventory.stock.read; the costing screens
    // need inventory.valuation.view, which an accountant holds without it.
    key: 'inventory',
    name: 'Inventory',
    description: 'Stock, movements, counts and valuation',
    tagline: 'Stock across warehouses',
    icon: 'app-inventory',
    module: 'inventory',
    home: '/inventory',
    sections: [
      {
        items: [{ to: '/inventory', label: 'Overview', icon: 'overview', permissions: ['inventory.stock.read'], end: true }],
      },
      {
        label: 'Stock',
        items: [
          { to: '/inventory/stock', label: 'Stock on hand', icon: 'stock', permissions: ['inventory.stock.read'], keywords: 'levels quantity bins' },
          { to: '/inventory/scanner', label: 'Barcode Scanner', icon: 'scan', permissions: ['inventory.stock.read'], keywords: 'barcode scanner qr putaway relocate pick' },
          { to: '/inventory/ledger', label: 'Stock ledger', icon: 'ledger', permissions: ['inventory.stock.read'], keywords: 'movements history' },
          { to: '/inventory/batches', label: 'Batches', icon: 'batch', permissions: ['inventory.stock.read'], keywords: 'lots expiry' },
          { to: '/inventory/serials', label: 'Serial numbers', icon: 'serial', permissions: ['inventory.stock.read'] },
          { to: '/inventory/reorder', label: 'Reorder suggestions', icon: 'reorder', permissions: ['inventory.stock.read'], keywords: 'replenish min max' },
        ],
      },
      {
        label: 'Operations',
        items: [
          { to: '/inventory/sales-orders', label: 'Order reservations', icon: 'reservation', permissions: ['inventory.stock.read'], keywords: 'allocate reserve' },
          { to: '/inventory/deliveries', label: 'Deliveries', icon: 'delivery', permissions: ['inventory.stock.read'], keywords: 'dispatch shipment pick pack' },
          { to: '/inventory/transfers', label: 'Transfers', icon: 'transfer', permissions: ['inventory.stock.read'], keywords: 'move warehouse' },
          { to: '/inventory/adjustments', label: 'Adjustments', icon: 'adjustment', permissions: ['inventory.stock.read'], keywords: 'write off damage' },
          { to: '/inventory/counts', label: 'Stock counts', icon: 'count', permissions: ['inventory.stock.read'], keywords: 'physical cycle count stocktake' },
          { to: '/inventory/returns', label: 'Customer returns', icon: 'return', permissions: ['inventory.stock.read'], keywords: 'rma' },
          { to: '/inventory/receipts', label: 'Stock receipts', icon: 'receipt', permissions: ['inventory.stock.read'], keywords: 'inbound put away' },
        ],
      },
      {
        label: 'Costing & reports',
        items: [
          { to: '/inventory/valuation', label: 'Valuation', icon: 'valuation', permissions: ['inventory.valuation.view'], keywords: 'value fifo average cost' },
          { to: '/inventory/landed-costs', label: 'Landed costs', icon: 'landed-cost', permissions: ['inventory.valuation.view'], keywords: 'freight duty' },
          { to: '/inventory/reports/abc', label: 'ABC analysis', icon: 'report', permissions: ['inventory.valuation.view'] },
          { to: '/inventory/reports/slow-moving', label: 'Slow-moving stock', icon: 'pie-chart', permissions: ['inventory.valuation.view'], keywords: 'ageing dead stock' },
          { to: '/inventory/reconciliations', label: 'Reconciliation', icon: 'reconcile', permissions: ['inventory.valuation.view'] },
        ],
      },
      {
        label: 'Setup',
        items: [
          { to: '/inventory/exceptions', label: 'Exceptions', icon: 'exception', permissions: ['inventory.stock.read'], keywords: 'failed events replay' },
          { to: '/inventory/settings', label: 'Inventory settings', icon: 'settings', permissions: ['inventory.stock.read'] },
          { to: '/warehouses', label: 'Warehouses', description: 'Warehouses, locations and bins', icon: 'warehouse', permissions: ['mdm.read'], shared: true },
          { to: '/items', label: 'Items', description: 'Products, services and stock items', icon: 'item', permissions: ['mdm.read'], shared: true },
        ],
      },
    ],
  },

  {
    key: 'sales',
    name: 'Sales',
    description: 'Quotations, orders, invoices and returns',
    tagline: 'From quote to cash',
    icon: 'app-sales',
    module: 'sales',
    home: '/sales',
    sections: [
      {
        items: [{ to: '/sales', label: 'Overview', icon: 'overview', permissions: ['sales.reports.read'], end: true }],
      },
      {
        label: 'Selling',
        items: [
          { to: '/quotations', label: 'Quotations', icon: 'quotation', permissions: ['sales.quote.read'], keywords: 'quote estimate proposal' },
          { to: '/sales-orders', label: 'Sales orders', icon: 'sales-order', permissions: ['sales.order.read'], keywords: 'so' },
          { to: '/sales-invoices', label: 'Invoices', icon: 'invoice', permissions: ['sales.invoice.read'], keywords: 'billing receivable' },
          { to: '/sales-recurring-invoices', label: 'Recurring invoices', icon: 'invoice', permissions: ['sales.invoice.read'], keywords: 'subscriptions recurring billing' },
          { to: '/sales-credit-notes', label: 'Credit notes', icon: 'credit', permissions: ['sales.invoice.read'], keywords: 'credit memo refund' },
          { to: '/sales-returns', label: 'Returns', icon: 'return', permissions: ['sales.rma.read'], keywords: 'rma credit' },
          { to: '/sales/reports', label: 'Reports & Analytics', icon: 'report', permissions: ['sales.reports.read'], keywords: 'sales metrics revenue performance' },
          { to: '/customers', label: 'Customers', description: 'Customers, terms and credit limits', icon: 'customer', permissions: ['mdm.read'], shared: true },
        ],
      },
      {
        label: 'Control',
        items: [
          { to: '/sales-approvals', label: 'Approvals', icon: 'approval', permissions: ['sales.quote.approve', 'sales.quote.reject', 'sales.order.amend'] },
          { to: '/sales-rules', label: 'Approval rules', icon: 'rule', permissions: ['sales.approval.manage'] },
          { to: '/sales-settings/credit', label: 'Credit check', icon: 'credit', permissions: ['sales.settings.manage'], keywords: 'credit limit hold' },
        ],
      },
    ],
  },

  {
    key: 'finance',
    name: 'Finance',
    description: 'Ledger, receivables, payables, banking and tax',
    tagline: 'Books, banking and tax',
    icon: 'app-finance',
    home: '/accounting',
    sections: [
      {
        items: [
          { to: '/accounting', label: 'Overview', icon: 'overview', permissions: ['accounting.account.read', 'accounting.journal.read', 'accounting.ar.read', 'accounting.ap.read', 'accounting.read'], end: true },
        ],
      },
      {
        label: 'Ledger',
        items: [
          {
            to: '/accounting/accounts',
            label: 'Chart of accounts',
            icon: 'accounts',
            permissions: ['accounting.account.read', 'accounting.read'],
            keywords: 'coa gl general ledger',
          },
          { to: '/accounting/journals', label: 'Journal entries', icon: 'journal', permissions: ['accounting.journal.read', 'accounting.read'], keywords: 'je' },
          { to: '/accounting/fiscal', label: 'Fiscal periods', icon: 'fiscal', permissions: ['accounting.period.read', 'accounting.read'], keywords: 'year close' },
        ],
      },
      {
        label: 'Money in & out',
        items: [
          { to: '/accounting/ar', label: 'Receivables', icon: 'receivable', permissions: ['accounting.ar.read', 'accounting.read'], keywords: 'ar debtors' },
          { to: '/accounting/ap', label: 'Payables', icon: 'payable', permissions: ['accounting.ap.read', 'accounting.read'], keywords: 'ap creditors' },
          {
            to: '/accounting/banking',
            label: 'Banking & reconciliation',
            icon: 'bank',
            permissions: ['accounting.banking.read', 'accounting.read'],
            keywords: 'bank statement recon',
          },
        ],
      },
      {
        label: 'Compliance & reporting',
        items: [
          { to: '/accounting/tax', label: 'Tax codes & reports', icon: 'tax', permissions: ['accounting.tax-code.read', 'accounting.read'], keywords: 'gst vat' },
          { to: '/accounting/fx', label: 'Currency & FX', icon: 'fx', permissions: ['accounting.exchange-rate.read', 'accounting.read'], keywords: 'exchange rate revaluation' },
          { to: '/accounting/reports', label: 'Financial reports', icon: 'report', permissions: ['accounting.account.read', 'accounting.read'], keywords: 'p&l balance sheet trial balance' },
        ],
      },
      {
        label: 'Automation',
        items: [
          { to: '/accounting/posting-rules', label: 'Posting rules', icon: 'posting-rule', permissions: ['accounting.posting-rule.read', 'accounting.read'] },
          {
            to: '/accounting/posting-exceptions',
            label: 'Posting exceptions',
            icon: 'exception',
            permissions: ['accounting.posting-exception.read', 'accounting.read'],
          },
        ],
      },
    ],
  },

  {
    key: 'crm',
    name: 'CRM',
    description: 'Leads, opportunities, contacts and campaigns',
    tagline: 'Leads and deals',
    icon: 'app-crm',
    module: 'crm',
    home: '/crm/dashboard',
    sections: [
      {
        items: [
          {
            to: '/crm/dashboard',
            label: 'Overview',
            icon: 'overview',
            permissions: ['crm.lead.read', 'crm.opportunity.read', 'crm.contact.read', 'crm.activity.read', 'crm.campaign.read'],
            end: true,
          },
        ],
      },
      {
        label: 'Pipeline',
        items: [
          { to: '/crm/leads', label: 'Leads', icon: 'lead', permissions: ['crm.lead.read'], keywords: 'prospects' },
          {
            to: '/crm/opportunities/all-boards',
            label: 'Opportunities',
            icon: 'opportunity',
            permissions: ['crm.opportunity.read'],
            match: ['/crm/opportunities'],
            keywords: 'deals boards kanban',
            children: [
              { to: '/crm/opportunities/all-boards', label: 'All boards' },
              { to: '/crm/opportunities/new-board', label: 'New board' },
            ],
          },
          { to: '/crm/contacts', label: 'Contacts', icon: 'contact', permissions: ['crm.contact.read'], keywords: 'people address book' },
        ],
      },
      {
        label: 'Engagement',
        items: [
          { to: '/crm/activities', label: 'Activities', icon: 'activity', permissions: ['crm.activity.read'], keywords: 'tasks calls' },
          { to: '/crm/meetings', label: 'Meetings', icon: 'meeting', permissions: ['crm.activity.read'], keywords: 'calendar' },
          { to: '/crm/campaigns', label: 'Campaigns', icon: 'campaign', permissions: ['crm.campaign.read'], keywords: 'marketing' },
        ],
      },
    ],
  },

  {
    key: 'people',
    name: 'People',
    description: 'Self-service, teams, attendance, leave and payroll',
    tagline: 'Your team and HR',
    icon: 'app-people',
    home: '/people',
    sections: [
      {
        label: 'My workplace',
        items: [
          { to: '/my/attendance', label: 'Attendance', description: 'Check in and out, and see your attendance record', icon: 'attendance', keywords: 'check in clock' },
          { to: '/my/tasks', label: 'My tasks', description: 'Tasks assigned to you, priorities and deadlines', icon: 'timesheet', keywords: 'tasks todo assignments' },
          { to: '/my/leaves', label: 'Leaves', description: 'Apply for leave and see what you have left', icon: 'leave', keywords: 'time off holiday vacation' },
          { to: '/my/timesheets', label: 'Timesheets', description: 'Log your hours against projects and tasks', icon: 'timesheet' },
          { to: '/my/payslips', label: 'Payslips', description: 'Your payslips, month by month', icon: 'payslip', keywords: 'salary' },
          { to: '/my/expenses', label: 'Expenses', description: 'Claim expenses and follow their approval', icon: 'expense', keywords: 'claims reimbursement' },
          { to: '/my/profile', label: 'Profile', description: 'Your personal, job and bank details', icon: 'profile' },
        ],
      },
      {
        label: 'My team',
        items: [
          { to: '/team/dashboard', label: 'Team dashboard', description: 'Who is in, who is away and what needs you', icon: 'team', permissions: ['hr.team.read'], match: ['/team/members'] },
          { to: '/team/tasks', label: 'Team tasks', description: 'Assign tasks to direct reports and follow progress', icon: 'timesheet', permissions: ['hr.team.read'] },
          { to: '/team/attendance', label: 'Team attendance', description: 'Your team’s attendance, day by day', icon: 'attendance', permissions: ['hr.team.read'] },
          { to: '/team/approvals', label: 'Team approvals', description: 'Leave, timesheets and expenses waiting on you', icon: 'team-approvals', permissions: ['hr.team.read'], badge: 'approvals' },
        ],
      },
      {
        label: 'HR administration',
        items: [
          { to: '/employees', label: 'Employees', description: 'The employee directory and records', icon: 'employee', permissions: ['hr.read'], keywords: 'staff directory' },
          { to: '/separations', label: 'Separations & Exits', description: 'Employee resignations, notice periods and clearances', icon: 'log-out', permissions: ['hr.read'], keywords: 'separations exits resignations notice exit clearance lwd' },
          { to: '/attendance', label: 'Attendance', description: 'Attendance across the organization', icon: 'attendance', permissions: ['hr.read'] },
          { to: '/timesheets', label: 'Timesheets', description: 'Everyone’s logged hours', icon: 'timesheet', permissions: ['hr.read'] },
          { to: '/leaves', label: 'Leaves', description: 'Leave requests and their decisions', icon: 'leave', permissions: ['hr.read'] },
          { to: '/leave-types', label: 'Leave policies', description: 'Leave types, entitlements and rules', icon: 'leave-policy', permissions: ['hr.read'] },
          { to: '/payroll', label: 'Payroll & salaries', description: 'Salary components and structures', icon: 'payroll', permissions: ['hr.read'], keywords: 'salary components' },
          { to: '/pay-runs', label: 'Pay runs', description: 'Run, review and publish payroll', icon: 'pay-run', permissions: ['hr.read'] },
          { to: '/expenses', label: 'Expense claims', description: 'Expense claims from every employee', icon: 'expense', permissions: ['hr.read'] },
          { to: '/holidays', label: 'Holiday calendar', description: 'The holiday calendar', icon: 'holiday', permissions: ['hr.read'] },
          { to: '/shifts', label: 'Shifts & rosters', description: 'Shifts, rosters and assignments', icon: 'shift', permissions: ['hr.read'] },
          { to: '/document-types', label: 'Document types', description: 'The documents employees must hold', icon: 'document', permissions: ['hr.read'] },
          { to: '/teams', label: 'Teams', description: 'Functional units and team leads', icon: 'team', permissions: ['hr.read'], keywords: 'teams squads groups' },
          { to: '/org-chart', label: 'Organization chart', description: 'Interactive visual hierarchy', icon: 'department', permissions: ['hr.read'], keywords: 'hierarchy tree org chart' },
          { to: '/departments', label: 'Departments', description: 'How the organization is divided', icon: 'department', permissions: ['hr.read'] },
          { to: '/designations', label: 'Designations', description: 'Job titles and grades', icon: 'designation', permissions: ['hr.read'], keywords: 'job titles' },
        ],
      },
    ],
  },

      {
    key: 'master-data',
    name: 'Master Data',
    description: 'Items, business partners, warehouses and shared reference data',
    tagline: 'Items and reference data',
    icon: 'app-master-data',
    home: '/master-data',
    sections: [
      {
        label: 'Catalog',
        items: [
          { to: '/items', label: 'Items', description: 'Products, services and stock items', icon: 'item', permissions: ['mdm.read', 'mdm.item.read'], keywords: 'products sku services catalog' },
          { to: '/price-lists', label: 'Price lists', description: 'Prices by customer, tier and date', icon: 'price-list', permissions: ['mdm.read', 'mdm.price.read'], keywords: 'pricing' },
          { to: '/uoms', label: 'Units of measure', description: 'Units of measure and conversions', icon: 'uom', permissions: ['mdm.read', 'mdm.uom.read'], keywords: 'uom conversion' },
        ],
      },
      {
        label: 'Parties & places',
        items: [
          {
            to: '/partners',
            label: 'Business Partners',
            description: 'Customers, vendors and dual-role partners',
            icon: 'customer',
            permissions: ['mdm.read', 'mdm.partner.read'],
            match: ['/customers', '/vendors', '/dual-partners'],
            keywords: 'partners business-partners customers vendors suppliers clients',
          },
          { to: '/warehouses', label: 'Warehouses', description: 'Warehouses, locations and bins', icon: 'warehouse', permissions: ['mdm.read', 'mdm.warehouse.read'], keywords: 'locations stores' },
        ],
      },
      {
        label: 'Commercial terms',
        items: [
          { to: '/payment-terms', label: 'Payment terms', description: 'When invoices and bills fall due', icon: 'payment-term', permissions: ['mdm.read', 'mdm.partner.read'], keywords: 'net 30 credit days' },
          { to: '/shipping-methods', label: 'Shipping methods', description: 'Carriers and delivery methods', icon: 'shipping', permissions: ['mdm.read', 'mdm.partner.read'], keywords: 'freight carrier' },
          { to: '/terms-templates', label: 'Terms templates', description: 'Reusable terms and conditions', icon: 'template', permissions: ['mdm.read', 'mdm.partner.read'], keywords: 'conditions' },
        ],
      },
      {
        label: 'Finance reference',
        items: [
          { to: '/currencies', label: 'Currencies', description: 'Currencies and exchange rates', icon: 'currency', permissions: ['mdm.read', 'mdm.currency.read', 'accounting.exchange-rate.read'], keywords: 'exchange rates' },
          { to: '/tax-codes', label: 'Tax codes', description: 'Tax codes and their rates', icon: 'tax', permissions: ['mdm.read', 'mdm.tax.read', 'accounting.tax-code.read'], keywords: 'gst vat hsn' },
          { to: '/cost-centers', label: 'Cost centers', description: 'Where costs are booked', icon: 'cost-center', permissions: ['mdm.read', 'accounting.account.read'] },
        ],
      },
    ],
  },

  {
    key: 'analytics',
    name: 'Analytics & Intelligence',
    description: 'Executive scorecards, financial statements, AR/AP aging, spend analysis and AI forecasting',
    tagline: 'Business intelligence and insights',
    icon: 'app-analytics',
    home: '/analytics',
    sections: [
      {
        items: [
          {
            to: '/analytics',
            label: 'Executive Scorecard',
            icon: 'analytics',
            permissions: ['analytics.executive.read'],
            end: true,
            keywords: 'executive dashboard kpis metrics revenue profit bi scorecard',
          },
        ],
      },
      {
        label: 'Financial & Spend',
        items: [
          {
            to: '/analytics/financials',
            label: 'Financial Statements',
            icon: 'report',
            permissions: ['analytics.financial.read'],
            keywords: 'p&l balance sheet cash flow statement income gl',
          },
          {
            to: '/analytics/aging-spend',
            label: 'AR/AP Aging & Spend',
            icon: 'receivable',
            permissions: ['analytics.ar.read', 'analytics.spend.read', 'analytics.ap.read'],
            keywords: 'ar ap aging buckets customer vendor spend procurement breakdown',
          },
        ],
      },
      {
        label: 'Operations & Workforce',
        items: [
          {
            to: '/analytics/operations',
            label: 'Operations & HR Analytics',
            icon: 'employee',
            permissions: ['analytics.inventory.read', 'analytics.sales.read', 'analytics.hr.read'],
            keywords: 'inventory turnover dead stock workforce headcount payroll leave',
          },
        ],
      },
    ],
  },

  {
    key: 'admin',
    name: 'Administration',
    description: 'Organization, people, roles, settings and audit',
    tagline: 'Organization and access',
    icon: 'app-admin',
    home: '/admin',
    sections: [
      {
        label: 'Organization',
        items: [
          { to: '/organization', label: 'Profile', description: 'Company details, address and branding', icon: 'organization', permissions: ['tenant.update'], end: true, keywords: 'company details address logo' },
          { to: '/organization/branches', label: 'Branches', description: 'Branches and who works in each', icon: 'branch', permissions: ['branch.create', 'branch.update'], keywords: 'locations offices' },
        ],
      },
      {
        label: 'Access',
        items: [
          { to: '/users', label: 'People', description: 'Members, invitations and access', icon: 'people', permissions: ['member.create', 'member.update'], keywords: 'users members invite' },
          { to: '/roles', label: 'Roles', description: 'Roles and the permissions they grant', icon: 'role', permissions: ['role.read'], keywords: 'permissions rbac' },
        ],
      },
      {
        label: 'Configuration',
        items: [
          // settings.read too: that is where anyone asks for an optional module.
          {
            to: '/settings',
            label: 'Settings',
            description: 'Locale, numbering, tax and modules',
            icon: 'settings',
            permissions: ['settings.update', 'settings.read'],
            end: true,
            keywords: 'modules numbering locale currency tax',
          },
          { to: '/activity', label: 'Activity log', description: 'Every change, who made it and when', icon: 'activity-log', permissions: ['audit.read'], keywords: 'audit trail history' },
        ],
      },
    ],
  },
];

export const APPS_BY_KEY = Object.fromEntries(APPS.map((app) => [app.key, app]));

// --- names in the interface language ------------------------------------------

/**
 * The registry above stays in English: it is the fallback, the command center
 * matches on it (so an English keyword still works in Arabic), and pinned and
 * recent pages are stored by `to` and app key, never by a translated name.
 * What is SHOWN goes through these helpers, at render time, with the
 * component's own `t` — the translations live in the `navigation` namespace,
 * keyed by the app key, the section label and the item's path:
 *
 *   appName(app, t)          navigation:apps.<appKey>.name
 *   sectionLabel(app, s, t)  navigation:apps.<appKey>.sections.<section>
 *   itemLabel(item, t)       navigation:items.<path>.label
 *
 * `options` is passed through to t(), e.g. { bilingual: false } for a name
 * interpolated into another sentence.
 */

/** 'master-data' -> 'masterData', '/rfqs/intake/forms' -> 'rfqsIntakeForms', '/' -> 'home'. */
export function registryKey(text) {
  const key = String(text ?? '')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((word, index) =>
      index ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word.toLowerCase()
    )
    .join('');
  return key || 'home';
}

const english = (app) => app?.englishName ?? app?.name ?? '';

export function appName(app, t, options) {
  if (!app) return '';
  return t(`navigation:apps.${registryKey(app.key)}.name`, { defaultValue: english(app), ...options });
}

export function appDescription(app, t, options) {
  if (!app?.description) return undefined;
  return t(`navigation:apps.${registryKey(app.key)}.description`, { defaultValue: app.description, ...options });
}

export function appTagline(app, t, options) {
  if (!app?.tagline) return undefined;
  return t(`navigation:apps.${registryKey(app.key)}.tagline`, { defaultValue: app.tagline, ...options });
}

export function sectionLabel(app, section, t, options) {
  if (!section?.label) return null;
  return t(`navigation:apps.${registryKey(app?.key)}.sections.${registryKey(section.label)}`, {
    defaultValue: section.label,
    ...options,
  });
}

export function itemLabel(item, t, options) {
  if (!item) return '';
  return t(`navigation:items.${registryKey(item.to)}.label`, { defaultValue: item.label, ...options });
}

export function itemDescription(item, t, options) {
  if (!item?.description) return undefined;
  return t(`navigation:items.${registryKey(item.to)}.description`, { defaultValue: item.description, ...options });
}

/** A sub-page beneath an item (item.children). */
export function subItemLabel(child, t, options) {
  return t(`navigation:subItems.${registryKey(child.to)}`, { defaultValue: child.label, ...options });
}

/** Every item of an app, flattened (children excluded). */
export const appItems = (app) => app.sections.flatMap((section) => section.items);

/**
 * Screens that belong to no app: the workspace home and your own account.
 * They are searchable, but open without any app's navigation.
 */
export const WORKSPACE_PAGES = [
  { to: '/', label: 'Home', icon: 'home', end: true, keywords: 'dashboard workspace start' },
  { to: '/account', label: 'Account and password', icon: 'key', keywords: 'profile password security' },
  { to: '/activity?people=me', label: 'My activity', icon: 'activity-log', keywords: 'history audit what i did log' },
];

// --- matching a URL to an item and an app -------------------------------------

/**
 * A router location with its /org/<handle> prefix removed, so it can be
 * compared with the organization-relative paths this registry is written in.
 */
export function orgRelative(location) {
  const match = /^\/org\/[^/]+(\/.*)?$/.exec(location.pathname);
  return { pathname: match ? match[1] || '/' : location.pathname, search: location.search ?? '' };
}

const pathOf = (to) => String(to).split('?')[0];
const queryOf = (to) => new URLSearchParams(String(to).split('?')[1] ?? '');

/**
 * How strongly `to` (with its `end` and `match` rules) claims a location, as
 * the length of the matching prefix; 0 when it does not match at all.
 *
 * A destination with a query string (CRM's analytics tab) only matches when
 * the location carries the same parameters and then outranks the same path
 * without them — so the analytics item, not the overview, is the active one.
 */
export function matchStrength(item, pathname, search = '') {
  const path = pathOf(item.to);
  const wanted = queryOf(item.to);
  const current = new URLSearchParams(search);

  for (const [key, value] of wanted) {
    if (current.get(key) !== value) return 0;
  }
  const bonus = [...wanted.keys()].length ? 0.5 : 0;

  if (pathname === path) return path.length + 1 + bonus;
  if (!item.end && path !== '/' && pathname.startsWith(`${path}/`)) return path.length + bonus;

  for (const prefix of item.match ?? []) {
    const clean = prefix.replace(/\/$/, '');
    if (pathname === clean || pathname.startsWith(`${clean}/`)) return clean.length + bonus;
  }
  return 0;
}

/** The item a location belongs to within a list of items, or null. */
export function activeItemIn(items, pathname, search) {
  let best = null;
  let bestScore = 0;
  for (const item of items) {
    const score = matchStrength(item, pathname, search);
    if (score > bestScore) {
      best = item;
      bestScore = score;
    }
  }
  return best;
}

/**
 * Which app a location belongs to.
 *
 * The longest claim wins. A page listed in two apps (Customers is in Master
 * Data and in Sales as a shortcut) keeps you in the app you came from, so
 * opening a customer from Sales does not throw you into Master Data. The
 * workspace pages belong to no app.
 *
 * @returns {string|null} an app key
 */
export function resolveAppKey(pathname, search, previousKey) {
  if (pathname === '/' || pathname === '/account') return null;

  if (previousKey && APPS_BY_KEY[previousKey]) {
    const previous = APPS_BY_KEY[previousKey];
    const claims = [{ to: previous.home, end: true }, ...appItems(previous)];
    if (claims.some((item) => matchStrength(item, pathname, search) > 0)) return previousKey;
  }

  let bestKey = null;
  let bestScore = 0;
  for (const app of APPS) {
    const claims = [{ to: app.home, end: true }, ...appItems(app).filter((item) => !item.shared)];
    for (const item of claims) {
      const score = matchStrength(item, pathname, search);
      if (score > bestScore) {
        bestKey = app.key;
        bestScore = score;
      }
    }
  }
  return bestKey;
}
